import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Providers } from './providers';
import { AppShell } from './AppShell';
import { ErrorBoundary } from '@/lib/ui/ErrorBoundary';
import { useAuth } from '@/lib/auth/useAuth';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/ForgotResetPages';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { LoadingState } from '@/lib/ui/states';
import { MediaPickerHost } from '@/features/media/MediaPickerHost';
import { savePendingConsent } from '@/features/oauth/pendingConsent';

/*
 * Every route below the shell is code-split.
 *
 * The programme module — student records and cohorts — was split first and for
 * the sharpest reason: `students:read` is held by `admin` alone, and an editor
 * signing in to fix a paragraph should not download two list pages, two record
 * pages and twelve tabs to do it. The rest of the console followed, because the
 * eager chunk had grown to 841 kB — over Vite's own 500 kB warning — and none of
 * it is needed to paint the shell (F-21). Nobody opens eighteen pages in a
 * session; each arrives on the click that asks for it, behind the one Suspense
 * boundary in `SuspendedRoutes`.
 *
 * Three pages stay eager on purpose: the two auth screens, which are the first
 * paint for a signed-out visitor and sit OUTSIDE that boundary, and the
 * dashboard, which is where every signed-in session lands.
 */
const AccountPage = lazy(() =>
  import('@/features/auth/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const TwoFactorSetupPage = lazy(() => import('@/features/auth/TwoFactorSetupPage'));
const PagesListPage = lazy(() =>
  import('@/features/pages/PagesListPage').then((m) => ({ default: m.PagesListPage })),
);
const PageEditorPage = lazy(() =>
  import('@/features/pages/PageEditorPage').then((m) => ({ default: m.PageEditorPage })),
);
const FamiliesListPage = lazy(() =>
  import('@/features/families/FamiliesListPage').then((m) => ({ default: m.FamiliesListPage })),
);
const FamilyEditorPage = lazy(() =>
  import('@/features/families/FamilyEditorPage').then((m) => ({ default: m.FamilyEditorPage })),
);
const ContentListPage = lazy(() =>
  import('@/features/content/ContentListPage').then((m) => ({ default: m.ContentListPage })),
);
const ContentEditorPage = lazy(() =>
  import('@/features/content/ContentEditorPage').then((m) => ({ default: m.ContentEditorPage })),
);
const PageCopyListPage = lazy(() =>
  import('@/features/pageCopy/PageCopyListPage').then((m) => ({ default: m.PageCopyListPage })),
);
const PageCopyEditorPage = lazy(() =>
  import('@/features/pageCopy/PageCopyEditorPage').then((m) => ({
    default: m.PageCopyEditorPage,
  })),
);
const MediaLibraryPage = lazy(() =>
  import('@/features/media/MediaLibraryPage').then((m) => ({ default: m.MediaLibraryPage })),
);
const LeadsPage = lazy(() =>
  import('@/features/leads/LeadsPage').then((m) => ({ default: m.LeadsPage })),
);
const AuditPage = lazy(() =>
  import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const ApiTokensPage = lazy(() =>
  import('@/features/apiTokens/ApiTokensPage').then((m) => ({ default: m.ApiTokensPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const ImportNewPage = lazy(() =>
  import('@/features/imports/ImportWizardPage').then((m) => ({ default: m.ImportNewPage })),
);
const ImportPage = lazy(() =>
  import('@/features/imports/ImportWizardPage').then((m) => ({ default: m.ImportPage })),
);
const StudentsListPage = lazy(() =>
  import('@/features/students/StudentsListPage').then((m) => ({ default: m.StudentsListPage })),
);
const StudentRecordPage = lazy(() =>
  import('@/features/students/StudentRecordPage').then((m) => ({ default: m.StudentRecordPage })),
);
const DuplicatesPage = lazy(() =>
  import('@/features/students/DuplicatesPage').then((m) => ({ default: m.DuplicatesPage })),
);
const CohortsListPage = lazy(() =>
  import('@/features/cohorts/CohortsListPage').then((m) => ({ default: m.CohortsListPage })),
);
const CohortRecordPage = lazy(() =>
  import('@/features/cohorts/CohortRecordPage').then((m) => ({ default: m.CohortRecordPage })),
);
// Round 10, phase 24: the work screens.
const GroupPage = lazy(() =>
  import('@/features/cohorts/GroupPage').then((m) => ({ default: m.GroupPage })),
);
const NeedsActionPage = lazy(() =>
  import('@/features/work/NeedsActionPage').then((m) => ({ default: m.NeedsActionPage })),
);
const NumbersPage = lazy(() =>
  import('@/features/work/NumbersPage').then((m) => ({ default: m.NumbersPage })),
);
const SessionAttendancePage = lazy(() =>
  import('@/features/cohorts/SessionAttendancePage').then((m) => ({
    default: m.SessionAttendancePage,
  })),
);
// OAuth consent and its error page sit OUTSIDE the shell, beside /login: a user
// arriving from an AI app is mid-way through someone else's flow, and the
// sidebar would only invite them to wander off before answering (plan D-96).
const ConsentPage = lazy(() =>
  import('@/features/oauth/ConsentPage').then((m) => ({ default: m.ConsentPage })),
);
const OAuthErrorPage = lazy(() =>
  import('@/features/oauth/OAuthErrorPage').then((m) => ({ default: m.OAuthErrorPage })),
);
const ConnectedAppsPage = lazy(() =>
  import('@/features/oauth/ConnectedAppsPage').then((m) => ({ default: m.ConnectedAppsPage })),
);
const AiConnectionsPage = lazy(() =>
  import('@/features/oauth/AiConnectionsPage').then((m) => ({ default: m.AiConnectionsPage })),
);
const AlumniListPage = lazy(() =>
  import('@/features/alumni/AlumniListPage').then((m) => ({ default: m.AlumniListPage })),
);
const ComposePage = lazy(() =>
  import('@/features/emails/ComposePage').then((m) => ({ default: m.ComposePage })),
);
const EmailLogPage = lazy(() =>
  import('@/features/emails/EmailLogPage').then((m) => ({ default: m.EmailLogPage })),
);
const InterestLeadsPage = lazy(() =>
  import('@/features/leads/InterestLeadsPage').then((m) => ({ default: m.InterestLeadsPage })),
);
const CalendlyBookingsPage = lazy(() =>
  import('@/features/leads/CalendlyBookingsPage').then((m) => ({ default: m.CalendlyBookingsPage })),
);
const CampaignsListPage = lazy(() =>
  import('@/features/campaigns/CampaignsListPage').then((m) => ({ default: m.CampaignsListPage })),
);
const CampaignComposePage = lazy(() =>
  import('@/features/campaigns/CampaignComposePage').then((m) => ({
    default: m.CampaignComposePage,
  })),
);
const CampaignPreviewPage = lazy(() =>
  import('@/features/campaigns/CampaignPreviewPage').then((m) => ({
    default: m.CampaignPreviewPage,
  })),
);

/* Round 10, phase 26: the public-numbers check and the teacher's screens. */
const PublicNumbersCheckPage = lazy(() =>
  import('@/features/publicNumbers/PublicNumbersCheckPage').then((m) => ({
    default: m.PublicNumbersCheckPage,
  })),
);
const TeachHomePage = lazy(() =>
  import('@/features/teach/TeachHomePage').then((m) => ({ default: m.TeachHomePage })),
);
const TeachGroupPage = lazy(() =>
  import('@/features/teach/TeachGroupPage').then((m) => ({ default: m.TeachGroupPage })),
);
const TeachSessionPage = lazy(() =>
  import('@/features/teach/TeachSessionPage').then((m) => ({ default: m.TeachSessionPage })),
);

/**
 * `/` is the dashboard for every role but one. A teacher holds `teach:own` and
 * nothing else (D-201), so the dashboard would be a page of 403s; their home
 * is My groups.
 */
function HomeRoute() {
  const { role } = useAuth();
  return role === 'teacher' ? <Navigate to="/teach" replace /> : <DashboardPage />;
}

export function App() {
  return (
    /* The TRUE outermost net, above the providers themselves. Everything inside
       `<Providers>` — the query client, the toast host, the auth provider — renders,
       and a throw from any of them used to have no boundary above it at all, so it
       unmounted the root and left a white tab (F-28). Nothing below can catch a
       component mounted at provider level; this can. */
    <ErrorBoundary>
      <Providers>
        {/* Catches errors in the shell/auth chrome, below the providers. */}
        <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/forgot" element={<ForgotPasswordPage />} />
              <Route path="/reset" element={<ResetPasswordPage />} />
              {/* Outside the shell on purpose: an account without 2FA gets this
                  screen and nothing else, with no navigation to leave by (D-209). */}
              <Route
                path="/account/2fa-setup"
                element={
                  <RequireEnrolment>
                    <StandaloneSuspense>
                      <TwoFactorSetupPage />
                    </StandaloneSuspense>
                  </RequireEnrolment>
                }
              />
              <Route
                path="/oauth/consent"
                element={
                  <RequireAuth>
                    <StandaloneSuspense>
                      <ConsentPage />
                    </StandaloneSuspense>
                  </RequireAuth>
                }
              />
              <Route
                path="/oauth/error"
                element={
                  <StandaloneSuspense>
                    <OAuthErrorPage />
                  </StandaloneSuspense>
                }
              />
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <AppShell>
                      {/* Per-route boundary: a crash in one page is isolated to the content
                          area and clears automatically when the user navigates elsewhere. */}
                      <RouteErrorBoundary>
                        {/* The media/entity picker host is mounted HERE, not in
                            `Providers`, and the position is load-bearing: the picker
                            renders a `MediaBrowser`, which reads router state, so it
                            must be inside `<BrowserRouter>` — mounted above it, it
                            threw React's context invariant with no boundary in reach
                            and blanked the whole console the moment an editor pressed
                            "Choose" (F-28). Inside `RouteErrorBoundary` too, so a
                            future crash in the picker costs the content area and
                            clears on the next navigation. */}
                        <MediaPickerHost>
                          <SuspendedRoutes>
                            <Route path="/" element={<HomeRoute />} />
                            <Route path="/account" element={<AccountPage />} />
                            <Route
                              path="/account/connected-apps"
                              element={<ConnectedAppsPage />}
                            />
                            <Route path="/pages" element={<PagesListPage />} />
                            <Route path="/pages/:id" element={<PageEditorPage />} />
                            <Route path="/families" element={<FamiliesListPage />} />
                            <Route path="/families/:id" element={<FamilyEditorPage />} />
                            {/* Page copy is a Content surface, not a Settings tab. Declared
                                before the collection routes so `/content/pages` resolves here
                                rather than being read as a collection named "pages". */}
                            <Route path="/content/pages" element={<PageCopyListPage />} />
                            <Route
                              path="/content/pages/:namespace"
                              element={<PageCopyEditorPage />}
                            />
                            <Route path="/content/:collection" element={<ContentListPage />} />
                            <Route path="/content/:collection/:id" element={<ContentEditorPage />} />
                            <Route path="/media" element={<MediaLibraryPage />} />
                            {/* Round 10, phase 25: two lead screens that are not
                                submission types. Static segments outrank `:type`. */}
                            <Route path="/leads/interest" element={<InterestLeadsPage />} />
                            <Route path="/leads/calendly" element={<CalendlyBookingsPage />} />
                            <Route path="/leads/:type" element={<LeadsPage />} />
                            <Route path="/emails" element={<EmailLogPage />} />
                            <Route path="/emails/compose" element={<ComposePage />} />
                            <Route path="/students" element={<StudentsListPage />} />
                            <Route path="/duplicates" element={<DuplicatesPage />} />
                            <Route path="/needs-action" element={<NeedsActionPage />} />
                            <Route path="/numbers" element={<NumbersPage />} />
                            <Route path="/numbers/public" element={<PublicNumbersCheckPage />} />
                            {/* The teacher's console (D-201): their groups, a group's
                                sessions, a session's register. Nothing else is routed
                                for them; the server enforces the rest. */}
                            <Route path="/teach" element={<TeachHomePage />} />
                            <Route path="/teach/groups/:gid" element={<TeachGroupPage />} />
                            <Route path="/teach/sessions/:sid" element={<TeachSessionPage />} />
                            {/* `:tab` is a path segment, not a query parameter: a tab
                                of a student's record is a place, and a colleague must
                                be able to send a link to the attendance you are both
                                looking at. */}
                            <Route path="/students/:id" element={<StudentRecordPage />} />
                            <Route path="/students/:id/:tab" element={<StudentRecordPage />} />
                            <Route path="/imports/new" element={<ImportNewPage />} />
                            <Route path="/imports/:id" element={<ImportPage />} />
                            <Route path="/cohorts" element={<CohortsListPage />} />
                            {/* The register is declared BEFORE `/cohorts/:id/:tab`
                                so `/cohorts/x/sessions/3` is read as a register
                                rather than as a record page with a stray segment. */}
                            <Route
                              path="/cohorts/:id/sessions/:ordinal"
                              element={<SessionAttendancePage />}
                            />
                            <Route path="/cohorts/:id/groups/:gid" element={<GroupPage />} />
                            <Route path="/cohorts/:id" element={<CohortRecordPage />} />
                            <Route path="/cohorts/:id/:tab" element={<CohortRecordPage />} />
                            <Route path="/alumni" element={<AlumniListPage />} />
                            <Route path="/campaigns" element={<CampaignsListPage />} />
                            {/* `new` is declared before `:id` so composing a campaign
                                is never read as opening one called "new". */}
                            <Route path="/campaigns/new" element={<CampaignComposePage />} />
                            <Route path="/campaigns/:id/preview" element={<CampaignPreviewPage />} />
                            <Route path="/campaigns/:id" element={<CampaignComposePage />} />
                            <Route path="/audit" element={<AuditPage />} />
                            <Route path="/users" element={<UsersPage />} />
                            <Route path="/tokens" element={<ApiTokensPage />} />
                            <Route path="/ai-connections" element={<AiConnectionsPage />} />
                            <Route path="/settings/*" element={<SettingsPage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                          </SuspendedRoutes>
                        </MediaPickerHost>
                      </RouteErrorBoundary>
                    </AppShell>
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </Providers>
    </ErrorBoundary>
  );
}

/**
 * `<Routes>` with the boundary the lazy routes need. It is here rather than
 * around each lazy element so a chunk that is still arriving shows the same
 * skeleton the page itself would, in the same place, instead of collapsing the
 * content area to nothing for a frame.
 */
function SuspendedRoutes({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Suspense fallback={<LoadingState label={t('common.loading')} />}>
      <Routes>{children}</Routes>
    </Suspense>
  );
}

/** Suspense for a lazy page that renders without the shell (the OAuth screens). */
function StandaloneSuspense({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-ink-navy">
          <span
            aria-label={t('common.loading')}
            className="h-[28px] w-[28px] animate-spin rounded-pill border-2 border-canvas-bone border-e-transparent"
          />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/** Route-scoped error boundary that resets whenever the path changes. */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

/** Auth guard: shows a splash while bootstrapping, redirects to login preserving deep-link. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { status, reconnecting, setPendingDeepLink } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    // A boot that is merely slow shows the spinner alone. One that is being
    // RETRIED — the server is rate-limiting or restarting — says so, because the
    // alternative is a spinner that looks hung for up to a minute and a half and
    // invites a reload, which spends another request against the same limit.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-md bg-canvas-sand px-md text-center">
        <span
          aria-label={t('common.loading')}
          className="h-[28px] w-[28px] animate-spin rounded-pill border-2 border-accent-blue border-e-transparent"
        />
        {reconnecting && (
          <div role="status" aria-live="polite" className="flex flex-col gap-2xs">
            <p className="text-component-title text-ink-deep">{t('common.reconnecting')}</p>
            <p className="max-w-sm text-small text-ink-mid">{t('common.reconnectingBody')}</p>
          </div>
        )}
      </div>
    );
  }
  if (status === 'unauthenticated') {
    const deepLink = location.pathname + location.search;
    if (deepLink !== '/') setPendingDeepLink(deepLink);
    // The in-memory deep link does not survive a reload on the login page. An
    // OAuth consent URL must, or the AI app is left waiting on a request id the
    // user cannot get back to — so that one path is also kept per tab.
    savePendingConsent(deepLink);
    return <Navigate to="/login" replace />;
  }
  // Signed in, but the account has no 2FA yet: every console route leads to the
  // setup screen until it is on (D-209).
  if (status === 'enrolling') return <Navigate to="/account/2fa-setup" replace />;
  return <>{children}</>;
}

/** The 2FA setup screen is for an enrolment-only session and nobody else. */
function RequireEnrolment({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'enrolling') return <>{children}</>;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  // Still booting: the ordinary guard draws the splash.
  return <RequireAuth>{null}</RequireAuth>;
}
